import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { TeamsService } from '../../services/teams/teams.service';
import { SessionService } from '../../services/session/session.service';
import { SpinnerService } from '../../services/spinner/spinner.service';
import { FeatureFlagService } from 'src/app/services/feature-flag/feature-flag.service';
import { NativeTeamContextService } from '../../services/native-team-context/native-team-context.service';

@Component({
    selector: 'app-analytics',
    templateUrl: './analytics.component.html',
    styleUrls: ['./analytics.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AnalyticsComponent implements OnInit, OnDestroy {

    public allLinks = [
        { title: 'All Teams Overview', link: '/analytics/overview' },
        { title: 'Current Team Overview', link: '/analytics/team-overview' },
        { title: 'Fund Allocation Overview', link: '/analytics/allocation-report' },
        { title: 'Allowance Allocation', link: '/analytics/allowance-allocation-report' },
        { title: 'Rewards Report', link: '/analytics/rewards-report' },
        { title: 'Spending Report', link: '/analytics/spending-report' },
        { title: 'Payment Report', link: '/analytics/payment-report' },
        { title: 'Members', link: '/analytics/agency-members' },
        { title: 'Categories', link: '/analytics/categories' },
        { title: 'Reimbursements', link: '/analytics/reimbursements' },
        { title: 'All Teams Allowances', link: '/analytics/allowances' },
        { title: 'Current Team Allowances', link: '/analytics/team-allowances' },
        { title: 'Peer To Peer', link: '/analytics/peer-to-peer' }
    ];
    public links: { title: string, link: string }[] = this.allLinks;
    public activeLink: any;
    public analytics: any;
    public areReportsEnabled: boolean = false;
    public isNativeTeamContextActive = false;
    public nativeTeams: any[] = [];
    public nativeTeamContextReady = false;
    public analyticsLoading = false;
    public analyticsLoadError = '';
    public selectedTeamId = '';
    private analyticsRequestVersion = 0;
    private wellspaceSubscription?: Subscription;
    private nativeTeamContextSubscription?: Subscription;

    constructor(
        private router: Router,
        private teamsService: TeamsService,
        private sessionService: SessionService,
        private spinnerService: SpinnerService,
        private featureFlagService: FeatureFlagService,
        private nativeTeamContextService: NativeTeamContextService,
    ) { 
        this.checkReportsEnabled();
    }

    async ngOnInit(): Promise<void> {
        const parsedUrl = this.router.parseUrl(this.router.url);
        const urlSegments = parsedUrl.root.children.primary?.segments || [];
        const analyticsPath = `/${urlSegments.slice(0, 2).map(segment => segment.path).join('/')}`;
        this.activeLink = this.links.find(link => link.link === analyticsPath) || this.links[0];

        const nativeTeamState = await this.nativeTeamContextService.initialize();
        this.applyNativeTeamState(nativeTeamState);
        this.nativeTeamContextReady = true;
        this.nativeTeamContextSubscription = this.nativeTeamContextService
            .stateChanges()
            .subscribe(state => this.applyNativeTeamState(state));

        this.wellspaceSubscription = this.teamsService.wellspaceChangedObservable.subscribe(wellspace => {
            const teamId = this.getValidatedTeamId(wellspace);
            if (!teamId) {
                this.clearAnalyticsState();
                return;
            }
            this.selectedTeamId = teamId;
            void this.loadAnalytics(teamId);
        });
    }

    ngOnDestroy(): void {
        this.analyticsRequestVersion++;
        this.wellspaceSubscription?.unsubscribe();
        this.nativeTeamContextSubscription?.unsubscribe();
    }

    get selectedNativeTeam(): any | undefined {
        return this.nativeTeams.find(team => `${team?.id || ''}`.trim() === this.selectedTeamId);
    }

    onNativeTeamChange(teamId: string): void {
        const normalizedTeamId = `${teamId || ''}`.trim();
        if (
            normalizedTeamId === this.selectedTeamId
            || !this.nativeTeams.some(team => `${team?.id || ''}`.trim() === normalizedTeamId)
        ) {
            return;
        }
        this.clearAnalyticsState();
        this.selectedTeamId = normalizedTeamId;
        void this.nativeTeamContextService.selectTeam(normalizedTeamId);
    }

    async checkReportsEnabled() {
        this.areReportsEnabled = await this.featureFlagService.isEnabled('REPORTS');
        if (!this.areReportsEnabled) {
            this.links = this.allLinks.filter(link => {
                return ![
                    '/analytics/allowance-allocation-report',
                    '/analytics/rewards-report',
                    '/analytics/payment-report',
                    '/analytics/spending-report'
                ].includes(link.link);
            });
        } else {
            this.links = this.allLinks;
        }
    }

    private applyNativeTeamState(state: any): void {
        this.isNativeTeamContextActive = state.active === true;
        this.nativeTeams = this.isNativeTeamContextActive && Array.isArray(state.teams)
            ? [...state.teams]
            : [];
    }

    private getValidatedTeamId(wellspace: any): string {
        const teamId = `${wellspace?.id || ''}`.trim();
        if (!teamId) {
            return '';
        }
        if (
            this.isNativeTeamContextActive
            && !this.nativeTeams.some(team => `${team?.id || ''}`.trim() === teamId)
        ) {
            return '';
        }
        return teamId;
    }

    private clearAnalyticsState(): void {
        this.analyticsRequestVersion++;
        this.selectedTeamId = '';
        this.analytics = undefined;
        this.analyticsLoading = false;
        this.analyticsLoadError = '';
        this.spinnerService.hide();
    }

    private async loadAnalytics(teamId: string): Promise<void> {
        if (!teamId) {
            this.clearAnalyticsState();
            return;
        }
        const requestVersion = ++this.analyticsRequestVersion;
        this.analytics = undefined;
        this.analyticsLoading = true;
        this.analyticsLoadError = '';
        this.spinnerService.show();
        try {
            // @ts-ignore
            const analytics = await this.teamsService.getTeamAnalytics(
                this.sessionService.getUserId(),
                teamId,
            );
            if (
                requestVersion !== this.analyticsRequestVersion
                || this.selectedTeamId !== teamId
            ) {
                return;
            }
            this.analytics = analytics;
            await this.checkReportsEnabled();
        } catch (error) {
            if (
                requestVersion !== this.analyticsRequestVersion
                || this.selectedTeamId !== teamId
            ) {
                return;
            }
            this.analyticsLoadError = 'Unable to load Analytics for this Team.';
        } finally {
            if (
                requestVersion === this.analyticsRequestVersion
                && this.selectedTeamId === teamId
            ) {
                this.analyticsLoading = false;
                this.spinnerService.hide();
            }
        }
    }

}
